import type { FC, ReactNode } from 'react'

import { useEffect, useLayoutEffect, useRef } from 'react'
import { useRouter } from 'next/router'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'

type RevealProps = {
    children: ReactNode
    className?: string
}

type RevealVariant = {
    from: gsap.TweenVars
    duration?: number
    ease?: string
}

/** useLayoutEffect on the client, useEffect on the server, without the warning. */
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect

if (typeof window !== 'undefined') {
    gsap.registerPlugin(ScrollTrigger)
}

/**
 * Entrance variants. Distances, durations and easing follow the source site:
 * a soft rise with a long power2 tail, images scaling down into place.
 */
const VARIANTS: Record<string, RevealVariant> = {
    'fade-up': { from: { y: 48, opacity: 0 } },
    fade: { from: { opacity: 0 }, duration: 1 },
    'fade-down': { from: { y: -40, opacity: 0 } },
    'slide-left': { from: { x: 60, opacity: 0 } },
    'slide-right': { from: { x: -60, opacity: 0 } },
    zoom: { from: { scale: 0.92, opacity: 0 }, duration: 1 },
    image: { from: { scale: 1.12, opacity: 0 }, duration: 1.2, ease: 'power3.out' }
}

const DEFAULT_DURATION = 0.9
const DEFAULT_EASE = 'power2.out'
const START = 'top 88%'

export const Reveal: FC<RevealProps> = ({ children, className }): ReactNode => {
    const root = useRef<HTMLDivElement>(null)
    const router = useRouter()

    useIsomorphicLayoutEffect(() => {
        const element = root.current

        if (!element) {
            return
        }

        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches

        if (reduced) {
            element
                .querySelectorAll<HTMLElement>('[data-reveal], [data-hero-reveal], [data-reveal-stagger] > *')
                .forEach((node) => {
                    node.classList.add('is-revealed')
                    node.style.opacity = '1'
                })

            return
        }

        const context = gsap.context(() => {
            const play = (
                node: HTMLElement,
                targets: HTMLElement[],
                variantName: string,
                stagger: number
            ): void => {
                const variant = VARIANTS[variantName] ?? VARIANTS['fade-up']
                const delay = Number(node.dataset.revealDelay ?? 0)

                gsap.set(targets, { ...variant.from })

                gsap.to(targets, {
                    x: 0,
                    y: 0,
                    scale: 1,
                    opacity: 1,
                    duration: variant.duration ?? DEFAULT_DURATION,
                    ease: variant.ease ?? DEFAULT_EASE,
                    delay,
                    stagger,
                    scrollTrigger: {
                        trigger: node,
                        start: START,
                        once: true
                    },
                    onComplete: () => {
                        node.classList.add('is-revealed')
                        targets.forEach((target) => target.classList.add('is-revealed'))
                    }
                })
            }

            // Single-element entrances ---------------------------------------
            gsap.utils.toArray<HTMLElement>('[data-reveal]').forEach((node) => {
                play(node, [node], node.dataset.reveal || 'fade-up', 0)
            })

            // Staggered groups: the container stays put, its children animate --
            gsap.utils.toArray<HTMLElement>('[data-reveal-stagger]').forEach((node) => {
                const children = Array.from(node.children) as HTMLElement[]

                if (!children.length) {
                    return
                }

                play(
                    node,
                    children,
                    node.dataset.revealVariant || 'fade-up',
                    Number(node.dataset.revealStagger) || 0.1
                )
            })

            // Above-the-fold hero, played on load rather than on scroll -------
            const heroItems = gsap.utils.toArray<HTMLElement>('[data-hero-reveal]')

            if (heroItems.length) {
                gsap.set(heroItems, { y: 40, opacity: 0 })

                gsap.to(heroItems, {
                    y: 0,
                    opacity: 1,
                    duration: 1,
                    ease: 'power3.out',
                    stagger: 0.12,
                    delay: 0.15,
                    onComplete: () => heroItems.forEach((item) => item.classList.add('is-revealed'))
                })
            }

            // Number counters ------------------------------------------------
            gsap.utils.toArray<HTMLElement>('[data-counter]').forEach((node) => {
                const target = Number(node.dataset.counter ?? 0)
                const separator = node.dataset.counterSeparator === 'true'
                const counter = { value: 0 }

                gsap.to(counter, {
                    value: target,
                    duration: 2,
                    ease: 'power2.out',
                    scrollTrigger: {
                        trigger: node,
                        start: 'top 92%',
                        once: true
                    },
                    onUpdate: () => {
                        const rounded = Math.round(counter.value)

                        node.textContent = separator
                            ? rounded.toLocaleString('en-US')
                            : String(rounded)
                    }
                })
            })

            // Slow drift on large feature imagery ----------------------------
            gsap.utils.toArray<HTMLElement>('[data-parallax]').forEach((node) => {
                gsap.fromTo(
                    node,
                    { yPercent: -4 },
                    {
                        yPercent: 4,
                        ease: 'none',
                        scrollTrigger: {
                            trigger: node,
                            start: 'top bottom',
                            end: 'bottom top',
                            scrub: true
                        }
                    }
                )
            })
        }, element)

        // Trigger positions are measured before images have finished loading,
        // so re-measure once they have. On a client-side route change `load`
        // has already fired, hence the immediate refresh as well.
        const refresh = (): void => ScrollTrigger.refresh()

        refresh()
        window.addEventListener('load', refresh)

        return () => {
            window.removeEventListener('load', refresh)
            context.revert()
        }
    }, [router.asPath])

    return (
        <div ref={root} className={className}>
            {children}
        </div>
    )
}
